import { Delete } from '@mui/icons-material'
import { Grid } from '@mui/material'
import { Button, IconButton, TextFieldC } from 'components'
import { quantityIds } from 'config'
import { LengthField } from 'modules/orders/components/futureDeliveryModal/lengthField'
import {
  useCreateParticularsComposition,
  useLoadOrdersProductsParticularsOptions,
  useResolveQuantityExpression
} from 'modules/orders/hooks'
import { useOrderStore } from 'modules/orders/store'
import { useCallback, useEffect, useMemo } from 'react'

import { useFieldArray, useFormContext } from 'react-hook-form'
import { useItemConfigurationReplicateDrawerStore } from './itemConfigurationReplicateDrawerStore'
import { useDebounce, uuId } from 'utils'
import {
  createIdentifier,
  useCreateIdentifier
} from '../services/useCreateIdentifier'
import { interpretExpression } from '../helpers/interpretExpression'

type RowProps = {
  index: number
  isLoading?: boolean
  remove: (index?: number | number[] | undefined) => void
}

const Row = (props: RowProps) => {
  const { index, remove, isLoading } = props

  const product = useItemConfigurationReplicateDrawerStore.useProduct()
  // console.log({ product })

  const { watch, setValue, getValues } = useFormContext()

  // console.log(product)
  const form = useOrderStore.useForm()

  const currentRow = watch(`newConfigurations.${index}`)

  const particular = product?.product?.configuration?.particulars.find(
    (partic: any) => quantityIds.includes(partic.id)
  )

  // console.log({ particular123: particular })

  const { data: identifier } = useCreateIdentifier(
    product.configurationFormValues
  )

  const { data: particularsOptionsResp, isFetching } =
    useLoadOrdersProductsParticularsOptions({
      configurationId: product.configurationId,
      particularId: particular.contentType === 'L' ? '' : particular.id,
      configuration: identifier || '',
      productId: product?.product?.id,
      customerId: form?.customer?.id,
      enabled: true
    })

  const newQuantityUn = Number(currentRow.newQuantityUn || 0)

  const resolveFormValues = useMemo(() => {
    if (!product?.quantityRule) return {}
    const base = { ...(product?.configurationFormValues || {}) }
    if (particular?.id) {
      base[particular.id] =
        currentRow?.lengthField?.content ??
        currentRow?.lengthField ??
        base[particular.id]
    }
    return base
  }, [
    product?.quantityRule,
    product?.configurationFormValues,
    particular?.id,
    currentRow?.lengthField
  ])

  const debouncedResolveFormValues = useDebounce(resolveFormValues, 300)
  const debouncedQuantityUn = useDebounce(newQuantityUn, 300)

  const { data: resolvedData } = useResolveQuantityExpression({
    productId: product?.product?.id,
    configurationFormValues: debouncedResolveFormValues,
    quantityUn: debouncedQuantityUn
  })

  useEffect(() => {
    if (!product?.quantityRule) return
    const resolved = resolvedData?.data?.quantity
    if (resolved == null) return
    const next = Number(resolved).toFixed(2)
    const fieldName = `newConfigurations.${index}.quantity`
    if (getValues(fieldName) === next) return
    setValue(fieldName, next, { shouldDirty: false })
  }, [resolvedData, product?.quantityRule, index, getValues, setValue])

  return (
    <Grid
      item
      xs={12}
      container
      alignItems="start"
      justifyContent="space-between"
    >
      <Grid item xs={11} container spacing={2}>
        <Grid item xs={4}>
          <LengthField
            name={`newConfigurations.${index}.lengthField`}
            configuration={{
              ...particular,
              contents: particularsOptionsResp?.data || []
            }}
            loading={isFetching}
            disabled={currentRow?.configuration?.id}
          />
        </Grid>
        <Grid item xs={4}>
          <TextFieldC
            name={`newConfigurations.${index}.newQuantityUn`}
            label="Qtd. UN"
            normalize={(text) => text.replace(',', '.')}
            disabled={isLoading}
          />
        </Grid>
        <Grid item xs={4}>
          <TextFieldC
            name={`newConfigurations.${index}.quantity`}
            label="Qtd."
            disabled
          />
        </Grid>
      </Grid>
      <Grid item xs={1}>
        <IconButton onClick={() => remove(index)}>
          <Delete color="error" />
        </IconButton>
      </Grid>
    </Grid>
  )
}

export function ProductConfigurationReplicateForm({
  onStartGenerateConfig,
  onEndGenerateConfig
}: any) {
  const { watch, control, setValue, setError, clearErrors } = useFormContext()
  const product = useItemConfigurationReplicateDrawerStore.useProduct()
  const nextSequence = useOrderStore.useNextSequence()

  // console.log({ product })

  const particular = product?.product?.configuration?.particulars.find(
    (partic: any) => quantityIds.includes(partic.id)
  )

  // console.log({ particular })

  const {
    mutateAsync: createConfiguration,
    isLoading: isSubmittingConfiguration
  } = useCreateParticularsComposition()

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'newConfigurations'
  })
  const watchFieldArray = watch('newConfigurations')
  const controlledFields = fields.map((field, index) => {
    return {
      ...field,
      ...watchFieldArray[index]
    }
  })

  // console.log({ createConfiguration })

  // console.log(fields)
  const generateConfigs = useCallback(async () => {
    onStartGenerateConfig()
    try {
      await controlledFields.reduce(async (memo: any, row: any, index) => {
        // await memo

        if (!row?.configuration?.id) {
          const mergedValues = {
            ...product?.configurationFormValues,
            [particular.id]: row.lengthField
          }

          // Recalcular campos com expressão usando os novos valores
          const particulars = product?.product?.configuration?.particulars || []
          particulars.forEach((p: any) => {
            if (p.expression && mergedValues[p.id] !== undefined) {
              const result = interpretExpression(p.expression, mergedValues)
              mergedValues[p.id] = result.toFixed(2)
            }
          })

          // Reordenar conforme ordem original dos particulares
          const sortedByOriginalIndex = [...particulars].sort(
            (a: any, b: any) => a.originalIndex - b.originalIndex
          )
          const newConfigurationValues: any = {}
          sortedByOriginalIndex.forEach((p: any) => {
            if (mergedValues[p.id] !== undefined) {
              newConfigurationValues[p.id] = mergedValues[p.id]
            }
          })

          const lengthFieldValue = row?.lengthField
          if (
            !!particular.initRange &&
            !!particular.endRange &&
            !isNaN(lengthFieldValue) &&
            (lengthFieldValue < particular.initRange ||
              lengthFieldValue > particular.endRange)
          ) {
            setError(`newConfigurations.${index}.lengthField`, {
              message: 'Deve estar dentro da faixa de valores'
            })
          } else {
            clearErrors(`newConfigurations.${index}.lengthField`)
          }

          const particularComposition = await createIdentifier(
            newConfigurationValues
          )
          const response = await createConfiguration({
            particularComposition
          })

          // console.log({ response })

          setValue(`newConfigurations.${index}.configuration`, {
            ...response?.data,
            newConfigurationValues
          })

          const uuid = uuId()
          nextSequence(uuid)

          setValue(`newConfigurations.${index}.particular`, particular)
          setValue(`newConfigurations.${index}.uuid`, uuid)
        }
      }, [])
    } catch (e) {
      console.log(e)
      console.error('Generate configurationId error!')
    } finally {
      onEndGenerateConfig()
    }
  }, [
    onStartGenerateConfig,
    onEndGenerateConfig,
    controlledFields,
    product?.configurationFormValues,
    product?.product?.configuration?.particulars,
    particular,
    createConfiguration,
    setValue,
    nextSequence,
    setError,
    clearErrors
  ])

  return (
    <Grid item xs={12} container spacing={2} alignItems="center">
      {controlledFields?.map((field, index) => (
        <Row
          key={field.id}
          index={index}
          remove={remove}
          isLoading={isSubmittingConfiguration}
        />
      ))}
      <Grid item xs={12} container justifyContent="flex-end" spacing={2}>
        <Grid item>
          <Button onClick={generateConfigs} variant="outlined">
            Gerar configurações
          </Button>
        </Grid>
        <Grid item>
          <Button
            onClick={() =>
              append({
                newQuantityUn: '',
                lengthField: null,
                quantity: ''
              })
            }
          >
            Adicionar Configuração
          </Button>
        </Grid>
      </Grid>
    </Grid>
  )
}
